/*
 * chatAgentEMTFlow — scripted step engine for the agent-facilitated EMT collection flow.
 * Pure JS (no React, no network). ChatScreen.js drives it; SwipeCards.js renders the cards.
 * The emt-agent-confirm card handles the API call internally and reports the result via onAnswer.
 *
 * Steps: recipient-email → amount → message → confirm → (queued | cancelled)
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function startAgentEMTFlow() {
  return {
    nextState: { step: 'recipient-email', recipientEmail: null, amount: null, message: null },
    botMessage: "I'll collect the details and queue this for an agent to handle.\n\nWho should the agent send money to? Enter their email address.",
    card: {
      type: 'emt-free-text',
      field: 'recipientEmail',
      label: 'Recipient email',
      placeholder: 'email@example.com',
      inputType: 'email',
      validate: 'email',
      cta: 'Next',
    },
  };
}

export function advanceAgentEMTFlow(state, answer) {
  const { step } = state;

  switch (step) {
    case 'recipient-email': {
      const email = String(answer || '').trim();
      if (!EMAIL_RE.test(email)) {
        return {
          nextState: state,
          botMessage: `That doesn't look like a valid email address. Please try again.`,
          card: {
            type: 'emt-free-text',
            field: 'recipientEmail',
            label: 'Recipient email',
            placeholder: 'email@example.com',
            inputType: 'email',
            validate: 'email',
            cta: 'Next',
          },
        };
      }
      return {
        nextState: { ...state, step: 'amount', recipientEmail: email },
        botMessage: `Sending to **${email}**. How much should the agent send?`,
        card: {
          type: 'emt-free-text',
          field: 'amount',
          label: 'Amount',
          placeholder: '0.00',
          inputType: 'number',
          prefix: '$',
          validate: 'positive-number',
          cta: 'Next',
        },
      };
    }

    case 'amount': {
      const amount = parseFloat(answer);
      if (!(amount > 0)) {
        return {
          nextState: state,
          botMessage: 'Please enter a positive amount.',
          card: {
            type: 'emt-free-text',
            field: 'amount',
            label: 'Amount',
            placeholder: '0.00',
            inputType: 'number',
            prefix: '$',
            validate: 'positive-number',
            cta: 'Next',
          },
        };
      }
      return {
        nextState: { ...state, step: 'message', amount },
        botMessage: `Got it — **$${amount.toFixed(2)}**. Any note for the recipient? (optional)`,
        card: {
          type: 'emt-free-text',
          field: 'message',
          label: 'Note',
          placeholder: 'e.g. "Your half of rent"',
          inputType: 'text',
          skippable: true,
          cta: 'Next',
        },
      };
    }

    case 'message': {
      const message = typeof answer === 'string' && answer.trim() ? answer.trim() : null;
      return {
        nextState: { ...state, step: 'confirm', message },
        botMessage: "Here's what the agent will send — review and queue:",
        card: {
          type: 'emt-agent-confirm',
          recipientEmail: state.recipientEmail,
          amount: state.amount,
          message,
        },
      };
    }

    case 'confirm': {
      // answer is { confirmed: true, referenceNumber } or { confirmed: false }
      if (!answer?.confirmed) {
        return {
          nextState: { ...state, step: 'cancelled' },
          botMessage: "Cancelled — nothing was queued. Let me know if you need anything else.",
          card: null,
          done: true,
        };
      }
      return {
        nextState: { ...state, step: 'queued', referenceNumber: answer.referenceNumber },
        botMessage: `Your transfer request has been queued for an IVA agent to handle.`,
        card: {
          type: 'emt-agent-queued',
          recipientEmail: state.recipientEmail,
          amount: state.amount,
          referenceNumber: answer.referenceNumber,
        },
        done: true,
      };
    }

    default:
      return { nextState: state, botMessage: '', card: null, done: true };
  }
}
